import React, { useState, useEffect, useCallback, useRef } from 'react';
import { schemaSyncApi, databasesApi } from '../../services/api';
import {
  GitBranch,
  Play,
  Loader,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  RotateCcw,
  Info,
  Table,
  Columns,
  Key,
  ListFilter
} from 'lucide-react';


const CATEGORY_ICONS = {
  Table: Table,
  Column: Columns,
  Index: ListFilter,
  Constraint: Key
};

const SchemaSyncPage = () => {
  // Setup state
  const [databases, setDatabases] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null); // connStringName of selected DB
  const [environment, setEnvironment] = useState('preprod');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState(false);

  // Execute state
  const [dryRun, setDryRun] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const pollingRef = useRef(null);

  // Validation state
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedScript, setExpandedScript] = useState(false);
  const [expandedDrift, setExpandedDrift] = useState(false);

  const selectedProjectRef = useRef(selectedProject);
  selectedProjectRef.current = selectedProject;

  // ── Data loading ──────────────────────────────────────────────────
  const loadHistory = useCallback(async (project) => {
    if (!project) return;
    setHistoryLoading(true);
    try {
      const res = await schemaSyncApi.getHistory(project);
      setHistory(res.data || []);
    } catch {
      setHistory([]);
    }
    setHistoryLoading(false);
  }, []);

  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => setDatabases(res.data?.filter(d => d.connStringName) || []))
      .catch(() => setDatabases([]));
  }, []);

  useEffect(() => {
    if (selectedProject) loadHistory(selectedProject);
  }, [selectedProject, loadHistory]);

  // ── Polling ───────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const res = await schemaSyncApi.getStatus();
      setSyncStatus(res.data);
      if (res.data?.status === 'SUCCESS' || res.data?.status === 'FAILED') {
        stopPolling();
        const proj = selectedProjectRef.current;
        if (proj) loadHistory(proj);
      }
    } catch { /* ignore */ }
  }, [loadHistory, stopPolling]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollingRef.current = setInterval(pollStatus, 1500);
  }, [pollStatus]);

  useEffect(() => { return () => stopPolling(); }, [stopPolling]);

  useEffect(() => {
    pollStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Actions ───────────────────────────────────────────────────────
  const startSync = async () => {
    if (!selectedProject) return;
    try {
      const res = await schemaSyncApi.start({ Project: selectedProject, DryRun: dryRun, Environment: environment });
      setSyncStatus(res.data);
      startPolling();
      setTimeout(pollStatus, 500);
    } catch (err) {
      const msg = err.response?.data?.errorMessage || err.response?.data?.ErrorMessage || err.message;
      setSyncStatus({ status: 'FAILED', errorMessage: msg, project: selectedProject });
    }
  };

  const acknowledge = () => {
    setSyncStatus(null);
    setExpandedCategories({});
    setExpandedScript(false);
    setExpandedDrift(false);
    setDryRun(false);
  };

  // ── Helpers ───────────────────────────────────────────────────────
  const isRunning = syncStatus?.status === 'RUNNING';
  const isComplete = syncStatus?.status === 'SUCCESS' || syncStatus?.status === 'FAILED';
  const isCompleteForThisProject = isComplete && syncStatus?.project === selectedProject;
  const hasPendingAck = isComplete && syncStatus?.project && syncStatus.project !== selectedProject;
  const showExecuteTile = !!selectedProject;
  const showValidationTile = isCompleteForThisProject;
  const isDryRunResult = syncStatus?.currentStep?.includes('Dry run') || syncStatus?.currentStep?.includes('would be applied');

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return '--';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getResultsByCategory = () => {
    if (!syncStatus?.objectResults) return {};
    return syncStatus.objectResults.reduce((acc, r) => {
      if (!acc[r.category]) acc[r.category] = [];
      acc[r.category].push(r);
      return acc;
    }, {});
  };

  const getCategoryIcon = (cat) => {
    const Icon = CATEGORY_ICONS[cat] || Key;
    return <Icon size={14} />;
  };

  // ── Render ────────────────────────────────────────────────────────
  const resultsByCategory = getResultsByCategory();
  const categoryOrder = ['Table', 'Column', 'Index', 'Constraint'];

  return (
    <div className="dev-module sync-dashboard-page">
      <div className="dev-module-header">
        <div className="dev-module-header-left">
          <h1>
            <GitBranch size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Schema Synchronization
          </h1>
          {selectedProject && (
            <span className="dev-module-meta">
              Preprod_TMPLT → {environment === 'prod' ? 'Prod' : 'PreProd'}_{databases.find(d => d.connStringName === selectedProject)?.displayName || selectedProject}
            </span>
          )}
        </div>
      </div>

      <div className="dev-module-content">
        <div className="sync-steps-layout">

          {/* ═══════════ TILE 1: SETUP ═══════════ */}
          <div className="import-step-panel sync-setup-panel">
            <div className="import-step-header">
              <span className="import-step-number">1</span>
              <h3>Setup</h3>
            </div>
            <p className="import-step-desc">
              Select target database. Schema always syncs from <strong>Preprod_TMPLT</strong> (master).
            </p>

            {/* Project selector */}
            <div className="sync-db-selector">
              {databases.map(db => (
                <button
                  key={db.connStringName}
                  className={`sync-db-btn ${selectedProject === db.connStringName ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedProject(db.connStringName);
                    setExpandedCategories({});
                  }}
                  disabled={isRunning}
                >
                  <span className="sync-db-label">{db.displayName}</span>
                  <span className="sync-db-count" style={{ fontSize: '11px', opacity: 0.7 }}>{db.databaseName}</span>
                </button>
              ))}
              {databases.length === 0 && (
                <span style={{ fontSize: '12px', color: '#888' }}>Loading databases...</span>
              )}
            </div>

            {/* Environment toggle */}
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Target Environment</div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {['preprod', 'prod'].map(env => (
                  <button
                    key={env}
                    className={`sync-db-btn ${environment === env ? 'active' : ''}`}
                    onClick={() => setEnvironment(env)}
                    disabled={isRunning}
                    style={{ flex: 1 }}
                  >
                    {env === 'preprod' ? 'PreProd' : 'Prod'}
                  </button>
                ))}
              </div>
            </div>

            {/* History */}
            {selectedProject && (
              <div className="sync-history-section">
                <button
                  className="sync-history-toggle"
                  onClick={() => setExpandedHistory(!expandedHistory)}
                >
                  {expandedHistory ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <Clock size={14} />
                  History {historyLoading ? '...' : `(${history.length})`}
                </button>

                {expandedHistory && history.length > 0 && (
                  <div className="sync-history-table-wrap">
                    <table className="sync-history-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="center">Status</th>
                          <th className="right">Objects</th>
                          <th className="right">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.slice(0, 10).map((h, i) => (
                          <tr key={i}>
                            <td>{formatDate(h.started_At)}</td>
                            <td className="center">
                              <span className={`sync-status-badge ${h.error_Message === 'DRY RUN' ? 'dryrun' : h.status?.toLowerCase()}`}>
                                {h.error_Message === 'DRY RUN' ? 'DRY RUN' : h.status}
                              </span>
                            </td>
                            <td className="right">{h.objects_Applied ?? '--'}</td>
                            <td className="right">{formatDuration(h.duration_Sec)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {expandedHistory && history.length === 0 && !historyLoading && (
                  <p className="sync-no-history">No schema sync history for {selectedProject}.</p>
                )}
              </div>
            )}
          </div>

          {/* ═══════════ TILE 2: EXECUTE ═══════════ */}
          <div className={`import-step-panel sync-execute-panel ${isRunning ? 'running' : ''}`}>
            {showExecuteTile && (
              <>
                <div className="import-step-header">
                  <span className="import-step-number">2</span>
                  <h3>Execute</h3>
                </div>

                {/* Pending ack for another project */}
                {hasPendingAck && (
                  <div className="sync-pending-ack">
                    <AlertTriangle size={14} />
                    <span>
                      <strong>{syncStatus.project}</strong> sync {syncStatus.status === 'SUCCESS' ? 'completed' : 'failed'} —
                      switch back to review, or{' '}
                      <button className="sync-ack-inline" onClick={acknowledge}>dismiss</button>
                    </span>
                  </div>
                )}

                {/* Ready state */}
                {!isRunning && !isCompleteForThisProject && (
                  <>
                    <p className="import-step-desc">
                      Compare <strong>TMPLT</strong> schema against <strong>Preprod_{selectedProject}</strong> and apply any missing tables, columns, indexes, and constraints.
                    </p>

                    <div className="sync-action-row">
                      <label className="sync-dryrun-toggle">
                        <input
                          type="checkbox"
                          checked={dryRun}
                          onChange={(e) => setDryRun(e.target.checked)}
                        />
                        Dry run
                      </label>
                      <button className="import-execute-btn" onClick={startSync}>
                        <Play size={16} />
                        {dryRun ? 'Preview Changes' : 'Sync Schema'}
                      </button>
                    </div>

                    <div style={{ marginTop: '12px', fontSize: '12px', color: '#888', display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                      <Info size={13} style={{ flexShrink: 0, marginTop: '1px' }} />
                      <span>Views are excluded — use <strong>View Synchronization</strong> for views.</span>
                    </div>
                  </>
                )}

                {/* Running state */}
                {isRunning && (
                  <div className="sync-running-status">
                    <div className="sync-running-header">
                      <Loader size={18} className="spinning" />
                      <span>Syncing {syncStatus.project} schema...</span>
                    </div>

                    {syncStatus.currentStep && (
                      <div className="sync-current-step">{syncStatus.currentStep}</div>
                    )}

                    <div className="sync-running-stats">
                      <span><Clock size={13} /> {formatDuration(syncStatus.elapsedSeconds)}</span>
                    </div>

                    {syncStatus.objectsTotal > 0 && (
                      <div className="import-progress">
                        <div className="import-progress-bar">
                          <div
                            className="import-progress-fill"
                            style={{ width: `${Math.round((syncStatus.objectsCompleted / syncStatus.objectsTotal) * 100)}%` }}
                          />
                        </div>
                        <span className="import-progress-text">
                          {syncStatus.objectsCompleted}/{syncStatus.objectsTotal} objects ({Math.round((syncStatus.objectsCompleted / syncStatus.objectsTotal) * 100)}%)
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Complete brief */}
                {isCompleteForThisProject && (
                  <div className={`sync-complete-brief ${syncStatus.status.toLowerCase()}`}>
                    {syncStatus.status === 'SUCCESS' ? (
                      <CheckCircle size={18} className="icon-success" />
                    ) : (
                      <XCircle size={18} className="icon-fail" />
                    )}
                    <div>
                      <div className="sync-complete-label">
                        {syncStatus.status === 'SUCCESS' ? (isDryRunResult ? 'Dry Run Complete' : 'Schema Sync Complete') : 'Sync Failed'}
                      </div>
                      <div className="sync-complete-stats">
                        {formatDuration(syncStatus.elapsedSeconds)}
                        {syncStatus.objectsCompleted > 0 && ` • ${syncStatus.objectsCompleted} objects`}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* ═══════════ TILE 3: VALIDATION ═══════════ */}
          <div className="import-step-panel sync-validation-panel">
            {showValidationTile && (
              <>
                <div className="import-step-header">
                  <span className="import-step-number">3</span>
                  <h3>Results</h3>
                </div>

                {/* Overall result */}
                <div className={`sync-val-result ${syncStatus.status.toLowerCase()}`}>
                  <div className="sync-val-summary">
                    {syncStatus.status === 'SUCCESS' ? (
                      <>
                        <CheckCircle size={20} />
                        <span>
                          {isDryRunResult
                            ? `Dry run — ${syncStatus.objectResults?.length || 0} objects would be applied`
                            : syncStatus.objectResults?.length === 0
                              ? 'Schemas are in sync — no changes needed'
                              : `${syncStatus.objectResults?.length || 0} objects applied`}
                        </span>
                      </>
                    ) : (
                      <>
                        <XCircle size={20} />
                        <span>Sync failed</span>
                      </>
                    )}
                  </div>

                  {syncStatus.errorMessage && (
                    <div className="sync-val-error">
                      <AlertTriangle size={14} />
                      <span>{syncStatus.errorMessage}</span>
                    </div>
                  )}
                </div>

                {/* Results by category */}
                {syncStatus.objectResults?.length > 0 && (
                  <div className="sync-val-tiers">
                    {categoryOrder
                      .filter(cat => resultsByCategory[cat]?.length > 0)
                      .map(cat => {
                        const results = resultsByCategory[cat] || [];
                        const failCount = results.filter(r => !r.success).length;
                        const isExpanded = expandedCategories[cat];

                        return (
                          <div key={cat} className="sync-val-tier-group">
                            <div
                              className="sync-val-tier-header"
                              onClick={() => setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }))}
                            >
                              <span className="sync-val-tier-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {getCategoryIcon(cat)}
                                {cat}s
                              </span>
                              <span className="sync-val-tier-count">{results.length}</span>
                              <span className="sync-val-tier-status">
                                {failCount === 0 ? (
                                  <CheckCircle size={14} className="icon-success" />
                                ) : (
                                  <AlertTriangle size={14} className="icon-warning" />
                                )}
                              </span>
                              {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            </div>

                            {isExpanded && (
                              <div className="sync-val-tier-tables">
                                {results.map((r, i) => (
                                  <div key={i} className="sync-val-table-row">
                                    <span className="sync-val-table-icon">
                                      {r.success
                                        ? <CheckCircle size={14} className="icon-success" />
                                        : <XCircle size={14} className="icon-fail" />}
                                    </span>
                                    <span className="sync-val-table-name">{r.objectName}</span>
                                    <span className="sync-transform-badge straight" style={{ fontSize: '10px' }}>{r.action}</span>
                                    {r.error && (
                                      <span style={{ fontSize: '11px', color: '#c0392b', marginLeft: '6px' }} title={r.error}>
                                        ⚠ {r.error.substring(0, 60)}{r.error.length > 60 ? '…' : ''}
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}

                {/* Drift warnings */}
                {syncStatus.driftWarnings?.length > 0 && (
                  <div className="sync-val-tiers" style={{ marginTop: '8px' }}>
                    <div className="sync-val-tier-group">
                      <div
                        className="sync-val-tier-header"
                        onClick={() => setExpandedDrift(v => !v)}
                        style={{ background: 'rgba(230, 126, 34, 0.08)' }}
                      >
                        <span className="sync-val-tier-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#e67e22' }}>
                          <AlertTriangle size={14} />
                          Drift Warnings
                        </span>
                        <span className="sync-val-tier-count">{syncStatus.driftWarnings.length}</span>
                        {expandedDrift ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>

                      {expandedDrift && (
                        <div className="sync-val-tier-tables">
                          {syncStatus.driftWarnings.map((w, i) => (
                            <div key={i} className="sync-val-table-row">
                              <span className="sync-val-table-icon">
                                <AlertTriangle size={13} style={{ color: '#e67e22' }} />
                              </span>
                              <span className="sync-val-table-name" style={{ fontSize: '12px' }}>{w}</span>
                            </div>
                          ))}
                          <div style={{ padding: '6px 8px', fontSize: '11px', color: '#888', fontStyle: 'italic' }}>
                            Drift objects exist in target but not TMPLT. Not auto-removed — review manually.
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Delta script (dry run or expandable reference) */}
                {syncStatus.deltaScript && (
                  <div className="sync-val-tiers" style={{ marginTop: '8px' }}>
                    <div className="sync-val-tier-group">
                      <div
                        className="sync-val-tier-header"
                        onClick={() => setExpandedScript(v => !v)}
                      >
                        <span className="sync-val-tier-label">
                          {isDryRunResult ? 'Generated DDL Script' : 'Applied DDL Script'}
                        </span>
                        {expandedScript ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </div>

                      {expandedScript && (
                        <pre style={{
                          margin: '4px 0 0',
                          padding: '10px 12px',
                          background: '#1e1e1e',
                          color: '#d4d4d4',
                          fontSize: '11px',
                          borderRadius: '0 0 4px 4px',
                          overflowX: 'auto',
                          maxHeight: '400px',
                          overflowY: 'auto',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {syncStatus.deltaScript}
                        </pre>
                      )}
                    </div>
                  </div>
                )}

                {/* Acknowledge */}
                <button className="sync-acknowledge-btn" onClick={acknowledge}>
                  <RotateCcw size={16} />
                  Acknowledge &amp; Reset
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default SchemaSyncPage;
